
// Vehicle Efficiency is rated per ICE fuel type
// Source: RMI Internal Projections
// Residential and Transport Energy Use in India: Past Trend and Future Outlook, LBNL, 2005
import { zipWith } from 'lodash';
import { createSelector } from 'reselect';

import { compoundGrowthSeries } from '../../services/statistics';
import {
  getNumProjectionYears,
} from '../selectors';
import { IOwnershipMode } from './../types/city';
import { PROJECTION_YEAR_DELTA } from './index';

// Values for B7:B13
const BASE_EFFICIENCY: IOwnershipMode = {
  autoRickshaw: {
    commercial: 0.06
  },
  car: {
    commercial: 0.19,
    private: 0.19,
    public: 0,
  },
  publicTransit: {
    bus: 1.1,
    metro: 1.75,
  },
  twoWheel: {
    commercial: 0.03,
    private: 0.03,
  },
}

// Efficiency agnostic to fuel type (will be used by EVs)
// Only want to project out to 2035, no need to go past to 2038
export const getVehicleEfficiencyOverall = createSelector(
  [
    getNumProjectionYears,
  ],
  (numYears) => {
    const growth = -0.01; // Predicted to drop 1% per year, Vehicle Efficiency:B14

    const carSeries = compoundGrowthSeries(BASE_EFFICIENCY.car.commercial, growth, numYears);
    const autoRickshawSeries = compoundGrowthSeries(BASE_EFFICIENCY.autoRickshaw.commercial, growth, numYears);
    const twoWheelSeries = compoundGrowthSeries(BASE_EFFICIENCY.twoWheel.commercial, growth, numYears);
    const busSeries = compoundGrowthSeries(BASE_EFFICIENCY.publicTransit.bus, growth, numYears);
    const metroSeries = compoundGrowthSeries(BASE_EFFICIENCY.publicTransit.metro, growth, numYears);

    return zipWith(
      carSeries,
      autoRickshawSeries,
      twoWheelSeries,
      busSeries,
      metroSeries,
      (
        car: number,
        autoRickshaw: number,
        twoWheel: number,
        bus: number,
        metro: number) => {

        return {
          autoRickshaw: {
            commercial: autoRickshaw,
          },
          car: {
            commercial: car,
            private: car,
          },
          publicTransit: {
            bus,
            metro,
          },
          twoWheel: {
            commercial: twoWheel,
            private: twoWheel,
          }
        }
      });
  }
)

export const getVehicleEfficiencyGasoline = createSelector(
  [
    getNumProjectionYears,
  ],
  (numYears) => {

    const projectYears = numYears ; // starting from 2017??!! (not 2018 like everybody else)
    const growth = 0.01;

    const carSeries = compoundGrowthSeries(17, growth, projectYears);
    const autoRickshawSeries = compoundGrowthSeries(30, growth, projectYears);
    const twoWheelSeries = compoundGrowthSeries(55, growth, projectYears);
    const busSeries = compoundGrowthSeries(4.6, growth, projectYears);

    // Fill in some zeroes for the first 2 years, 2015 and 2016 as blank
    const allSeries = [carSeries, autoRickshawSeries, twoWheelSeries, busSeries];

    // Add items to beginning
    allSeries.forEach(series => {
      series.unshift(0);
      series.unshift(0);
    })

    return zipWith(
      carSeries,
      autoRickshawSeries,
      twoWheelSeries,
      busSeries,
      (
        car: number,
        autoRickshaw: number,
        twoWheel: number,
        bus: number) => {

        return {
          autoRickshaw: {
            commercial: autoRickshaw,
          },
          car: {
            commercial: car,
            private: car,
          },
          publicTransit: {
            bus,
          },
          twoWheel: {
            commercial: twoWheel,
            private: twoWheel,
          }
        }
      });
  }
)



//
const CNG_EFFICIENCY_GROWTH = {
  autoRickshaw: .0213801110444705,
  car: 0.0213253788075647,
  twoWheel: 0.021670372825515,
}

export const getVehicleEfficiencyCNG = createSelector(
  [
    getNumProjectionYears,
  ],
  (numYears) => {

    const projectYears = numYears + PROJECTION_YEAR_DELTA - 1; // starting from 2015, not 2018

    const carSeries = compoundGrowthSeries(9.4, CNG_EFFICIENCY_GROWTH.car, projectYears);
    const autoRickshawSeries = compoundGrowthSeries(24.10, CNG_EFFICIENCY_GROWTH.autoRickshaw, projectYears);
    // Checking with marshall if buses should have their own growth rate
    const busSeries = compoundGrowthSeries(2.9, CNG_EFFICIENCY_GROWTH.twoWheel, projectYears);

    return zipWith(
      carSeries,
      autoRickshawSeries,
      busSeries,
      (
        car: number,
        autoRickshaw: number,
        bus: number) => {

        return {
          autoRickshaw: {
            commercial: autoRickshaw,
          },
          car: {
            commercial: car,
            private: car,
          },
          publicTransit: {
            bus,
          },
        }
      });
  }
)

// conceptually, same function as the CNG one right above
const DIESEL_EFFICIENCY_GROWTH = {
  autoRickshaw: 0.0214408778043254 ,
  car: .0212897067946394,
  publicTransit: 0.0211875573750033,
}
export const getVehicleEfficiencyDiesel = createSelector(
  [
    getNumProjectionYears,
  ],
  (numYears) => {

    const projectYears = numYears + PROJECTION_YEAR_DELTA - 1; // starting from 2015, not 2018
    const carSeries = compoundGrowthSeries(14.8, DIESEL_EFFICIENCY_GROWTH.car, projectYears);
    const autoRickshawSeries = compoundGrowthSeries(37.9, DIESEL_EFFICIENCY_GROWTH.autoRickshaw, projectYears);
    const busSeries = compoundGrowthSeries(4.6, DIESEL_EFFICIENCY_GROWTH.publicTransit, projectYears);

    return zipWith(
      carSeries,
      autoRickshawSeries,
      busSeries,
      (
        car: number,
        autoRickshaw: number,
        bus: number) => {

        return {
          autoRickshaw: {
            commercial: autoRickshaw,
          },
          car: {
            commercial: car,
            private: car,
          },
          publicTransit: {
            bus,
          },
        }
      });
  }
)



// WEBPACK FOOTER //
// ./src/store/selectors/vehicleEfficiency.ts